import type { TourScene } from './tour-types';

export const LAB_SCENES: readonly TourScene[] = [
  {
    id: 'lab-login',
    chapter: 'l-start',
    route: '/login',
    title: 'Sign in',
    label: 'Sign in',
    caption: {
      heading: 'A laboratory has its own sign-in',
      body: 'A stand-alone lab signs in exactly like a hospital, with its own organisation name. Once you are in, the menu and the dashboard show only what a lab needs.',
      tip: 'Your provider licenses the modules for your plan. The menu simply follows it, so a lab is never buried under wards and theatres.',
    },
  },
  {
    id: 'lab-catalogue',
    chapter: 'l-start',
    route: '/lab/catalogue',
    title: 'Test catalogue',
    label: 'Catalogue',
    caption: {
      heading: 'The test catalogue',
      body: 'Every test has its department, result type, unit, reference range, turnaround time and price. Search by name or code, or open the Panels tab for bundles such as a complete blood count.',
      tip: 'A new lab arrives with a starter list, so the first order can be placed before anyone has typed a test in.',
    },
  },
  {
    id: 'lab-add-test',
    chapter: 'l-start',
    route: '/lab/catalogue',
    title: 'Add a test',
    label: 'Add a test',
    caption: {
      heading: 'Add a test of your own',
      body: 'Give it a code, a name, the specimen, the result type and unit, the turnaround and the price. You can also set critical low and high limits, mark it as sent out to a reference lab, or switch it off without deleting it.',
      tip: 'Reference ranges drive the low and high flags when results are typed, so enter them once and every report is checked the same way.',
    },
  },
  {
    id: 'lab-register',
    chapter: 'l-orders',
    route: '/patients/new',
    title: 'Register a walk-in',
    label: 'Register',
    caption: {
      heading: 'A walk-in is registered in seconds',
      body: 'A name and a mobile number is all the counter needs. Press Register and the patient gets a number. Everything else can be added later.',
      tip: 'The CNIC and mobile search finds a person who has visited before, so the same patient is never entered twice.',
    },
  },
  {
    id: 'lab-order',
    chapter: 'l-orders',
    route: '/lab',
    title: 'Order the tests',
    label: 'Order',
    caption: {
      heading: 'Choose the tests and place the order',
      body: 'Find the patient, search for tests or panels, and set the priority. Tick Bill now at the desk for a walk-in, place the order, and it lands on the worklist with an invoice already raised.',
      tip: 'The button counts the items in the order, so a wrong click is easy to spot before you place it.',
    },
  },
  {
    id: 'lab-desk',
    chapter: 'l-bench',
    route: '/billing',
    title: 'Take payment at the desk',
    label: 'Desk',
    caption: {
      heading: 'Take payment before the sample',
      body: 'The order already raised an invoice. Open it, press Collect payment, enter the cash tendered and the invoice moves to paid. The change is worked out for you.',
      tip: 'Open a cashier shift first, so the drawer can be counted at the end of the day.',
    },
  },
  {
    id: 'lab-collect',
    chapter: 'l-bench',
    route: '/lab',
    title: 'Collect and label',
    label: 'Collect',
    caption: {
      heading: 'Collect the specimens and label the tubes',
      body: 'Tick the ordered tests and press Collect. The lab allocates an accession number to each specimen and offers to print the tube labels. Then the tubes are received into the lab.',
      tip: 'The accession number is what travels with the tube, so a result can never be attached to the wrong patient.',
    },
  },
  {
    id: 'lab-results',
    chapter: 'l-bench',
    route: '/lab',
    title: 'Enter results',
    label: 'Results',
    caption: {
      heading: 'Type the results',
      body: 'Open the result sheet and type each value. It is checked against the reference range as you go, and low or high results are flagged before you save.',
      tip: 'Values and flags stay editable until the report is verified, so a mistyped number is a two-second fix.',
    },
  },
  {
    id: 'lab-release',
    chapter: 'l-bench',
    route: '/lab',
    title: 'Verify and release',
    label: 'Release',
    caption: {
      heading: 'Verify and release the report',
      body: 'Results wait in To verify for a second look. Verify and release makes them final, and the Released tab lists them with a print button for every result.',
      tip: 'Only people with the verify permission see the release button. Typing results and releasing them are different jobs.',
    },
  },
  {
    id: 'lab-billing',
    chapter: 'l-bench',
    route: '/billing',
    title: 'Billing',
    label: 'Billing',
    caption: {
      heading: 'The books stay straight',
      body: 'The invoice sits in the billing list with its patient, context, status and balance, so you can see at a glance what is paid and what is still owed.',
      tip: 'Filter by status to see what is still unpaid at the end of the day.',
    },
  },
  {
    id: 'lab-dashboard',
    chapter: 'l-bench',
    route: '/dashboard',
    title: 'Dashboard',
    label: 'Dashboard',
    caption: {
      heading: 'The day in four numbers',
      body: 'A laboratory’s dashboard shows new patients, money collected and the lab and imaging work still pending. There are no bed or clinic cards, because a lab has neither.',
      tip: 'Collected today counts what the cashier actually took, not what was billed.',
    },
  },
];

export const STORE_SCENES: readonly TourScene[] = [
  {
    id: 'store-login',
    chapter: 's-start',
    route: '/login',
    title: 'Sign in',
    label: 'Sign in',
    caption: {
      heading: 'A medical store signs in the same way',
      body: 'Type your organisation, username and password. A store gets its own menu: the pharmacy counter, inventory, billing and a handful of administration screens.',
      tip: 'Each person at the counter gets their own login, so every sale is recorded against the person who made it.',
    },
  },
  {
    id: 'store-products',
    chapter: 's-start',
    route: '/inventory',
    title: 'Products',
    label: 'Products',
    caption: {
      heading: 'The product list',
      body: 'Drugs and consumables are listed with their brand and generic names, form, strength, pack size, quantity on hand, reorder level and a prescription flag. Search by name, brand or code to find one quickly.',
      tip: 'A new store starts with a starter list of common medicines and consumables, so you can begin receiving stock on day one.',
    },
  },
  {
    id: 'store-supplier',
    chapter: 's-start',
    route: '/inventory',
    title: 'Add a supplier',
    label: 'Supplier',
    caption: {
      heading: 'Add your distributors',
      body: 'Every goods receipt and purchase order is raised against a supplier. Add each distributor once, with a code, a contact person, a phone number and payment terms.',
      tip: 'Untick Still buying from them to retire a supplier without losing their history.',
    },
  },
  {
    id: 'store-receive',
    chapter: 's-stock',
    route: '/inventory',
    title: 'Receive goods',
    label: 'Receive goods',
    caption: {
      heading: 'Receive a delivery in one form',
      body: 'Choose the store and the supplier, type their invoice number and add a line per product with batch number, expiry date, quantity, cost and retail price. Press Receive and the stock is on the shelf.',
      tip: 'Batch and expiry are required, so selling always takes the batch that expires first.',
    },
  },
  {
    id: 'store-control',
    chapter: 's-stock',
    route: '/inventory',
    title: 'Expiry, valuation and reorder',
    label: 'Expiry and reorder',
    caption: {
      heading: 'Keep the shelf honest',
      body: 'The Expiry tab counts what expires in 30, 60 and 90 days and lists what has already expired. Valuation shows the stock at cost and at retail, with your margin. Reorder lists what has fallen below its reorder level.',
      tip: 'Check Expiry once a week. Returning near-dated stock to the distributor is far cheaper than writing it off.',
    },
  },
  {
    id: 'store-sale',
    chapter: 's-sales',
    route: '/pharmacy',
    title: 'Counter sale',
    label: 'Sell',
    caption: {
      heading: 'Sell at the counter',
      body: 'Search a medicine by name or scan its barcode, set the quantity, type the customer’s name for a walk-in and take payment. For cash, the change is worked out from the amount tendered.',
      tip: 'F3 jumps to the search box and F2 takes payment, so a busy counter never needs the mouse.',
    },
  },
  {
    id: 'store-after-sale',
    chapter: 's-sales',
    route: '/inventory',
    title: 'Stock after the sale',
    label: 'Stock moves',
    caption: {
      heading: 'Stock moves with every sale',
      body: 'The quantity on hand drops the moment the sale is completed, batch by batch. Nobody has to update a register by hand.',
      tip: 'When a product falls below its reorder level it appears on the Reorder tab.',
    },
  },
  {
    id: 'store-dashboard',
    chapter: 's-sales',
    route: '/dashboard',
    title: 'Dashboard',
    label: 'Takings',
    caption: {
      heading: 'Takings at a glance',
      body: 'The dashboard shows what was collected today and how many invoices and how much was billed. Reports give the same figures by day, by mode and by receivable.',
      tip: 'Compare collected with billed. Any gap is money still owed to you.',
    },
  },
];
